/* Missing Bills — upload the daily sales file; only bills not yet in the portal are added
   as real transactions (Store KPI + every dashboard update). Reuses the Reco ingest. */
import { useState, useEffect, useCallback, useRef } from "react";
import api, { API_URL } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "./_shared";
import { UploadCloud, Download, RefreshCw } from "lucide-react";
import { MissingBillRuns } from "./_missing_bill_runs";

export default function MissingBills() {
  const [runs, setRuns] = useState([]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/reco/status");
      setRuns((r.data.runs || []).filter((x) => x.source === "missing_bills"));
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!runs.some((r) => r.status === "running")) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [runs, load]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("source", "missing_bills");
      await api.post("/reco/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000 });
      toast.success("Sales file queued", { description: "Adding only the bills that are missing." });
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
      await load();
    } catch (e) {
      toast.error("Upload failed", { description: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const template = () => {
    const token = localStorage.getItem("kazo_token");
    fetch(`${API_URL}/reco/template`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = "missing_bills_template.csv"; a.click(); URL.revokeObjectURL(u); });
  };

  return (
    <div data-testid="missing-bills-page">
      <PageHeader title="Missing Bills" subtitle="DATA · DAILY SALES UPLOAD"
        actions={<>
          <button onClick={template} className="k-btn k-btn-outline k-btn-sm" data-testid="missing-bills-template"><Download className="w-3.5 h-3.5" /> Template</button>
          <button onClick={load} className="k-btn k-btn-outline k-btn-sm" data-testid="missing-bills-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        </>} />
      <div className="p-8 space-y-6 max-w-5xl">
        <div className="chart-card p-5 space-y-4" data-accent="burgundy" data-testid="missing-bills-upload-card">
          <p className="text-sm text-neutral-600">
            Upload the daily sales file (CSV or Excel). Every bill that is <b>not already in the portal</b> is added as a real
            transaction — Store KPI, sales dashboards, customer visits and Fundle Brain update straight away. Bills already
            present are skipped, so the same file can be uploaded again safely.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" data-testid="missing-bills-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm text-neutral-600 file:mr-3 file:py-2 file:px-4 file:border-0 file:text-sm file:bg-neutral-900 file:text-white file:cursor-pointer" />
            <button onClick={upload} disabled={busy || !file} className="k-btn kazo-bg-burgundy text-white" data-testid="missing-bills-upload-btn">
              <UploadCloud className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Upload & add missing bills"}
            </button>
          </div>
        </div>
        <MissingBillRuns runs={runs} />
      </div>
    </div>
  );
}
